import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { TitleWithHelp } from '../components/ui/help-tooltip';
import api from '../lib/axios';
import { toast } from 'sonner';
import { PERIODOS, computeDateRange } from '../components/compras/ComprasFilters';
import {
  LifeBuoy, Loader2, Send, Paperclip, User, Headset, Inbox, RefreshCw, X,
  Search, Download, FilterX, ThumbsDown, ThumbsUp, AlertTriangle, ChevronRight,
  CircleDot, ClipboardList, Hourglass, CheckCircle2, ListFilter,
  Calendar as CalendarIcon, Filter, Check,
} from 'lucide-react';

const TIPO_LABEL = { duvida: 'Dúvida', erro: 'Erro', melhoria: 'Melhoria', analise_informacao: 'Análise de Informação', nova_funcionalidade: 'Nova Funcionalidade' };
const TIPO_COLOR = {
  duvida: 'bg-blue-100 text-blue-700 border-blue-200',
  erro: 'bg-red-100 text-red-700 border-red-200',
  melhoria: 'bg-purple-100 text-purple-700 border-purple-200',
  analise_informacao: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  nova_funcionalidade: 'bg-teal-100 text-teal-700 border-teal-200',
};
const STATUS_LABEL = { aberto: 'Aberto', em_analise: 'Em Análise', pendente_usuario: 'Pendente Usuário', resolvido: 'Resolvido' };
const STATUS_COLOR = {
  aberto: 'bg-amber-100 text-amber-700 border-amber-200',
  em_analise: 'bg-blue-100 text-blue-700 border-blue-200',
  pendente_usuario: 'bg-purple-100 text-purple-700 border-purple-200',
  resolvido: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};
const PRIORIDADE_LABEL = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgente: 'Urgente' };
const PRIORIDADE_COLOR = {
  baixa: 'bg-gray-100 text-gray-600 border-gray-200',
  media: 'bg-blue-100 text-blue-700 border-blue-200',
  alta: 'bg-orange-100 text-orange-700 border-orange-200',
  urgente: 'bg-red-100 text-red-700 border-red-200',
};
const FALLBACK_BADGE = 'bg-gray-100 text-gray-500 border-gray-200';

const STATUS_CARDS = [
  { key: 'aberto', label: 'Aberto', icon: CircleDot, text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', ring: 'ring-amber-300' },
  { key: 'em_analise', label: 'Em Análise', icon: ClipboardList, text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', ring: 'ring-blue-300' },
  { key: 'pendente_usuario', label: 'Pendente Usuário', icon: Hourglass, text: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', ring: 'ring-purple-300' },
  { key: 'resolvido', label: 'Resolvido', icon: CheckCircle2, text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-300' },
];

const rotulo = (mapa, chave, vazio) => (chave ? (mapa[chave] || chave) : vazio);
const cor = (mapa, chave) => (chave && mapa[chave]) || FALLBACK_BADGE;

const formatarData = (iso, curto = false) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return curto
      ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
      : d.toLocaleString('pt-BR');
  } catch {
    return '—';
  }
};

const toDateInputValue = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Pill de filtro single-select (mesmo padrão visual dos filtros de Compras). */
const FiltroPill = ({ label, allLabel, icon: Icon = Filter, value, allValue = 'todos', options, onChange, testId }) => {
  const ativo = value !== allValue;
  const opt = options.find((o) => o.value === value);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-9 ${ativo ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'bg-white'}`}
          data-testid={testId}
        >
          <Icon className="w-3.5 h-3.5 mr-1.5" />
          {ativo ? (opt?.label || value) : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2" align="start">
        <div className="space-y-0.5 max-h-72 overflow-y-auto">
          <button
            type="button"
            onClick={() => onChange(allValue)}
            className={`w-full text-left px-2 py-1.5 text-xs rounded flex items-center justify-between hover:bg-slate-100 ${value === allValue ? 'bg-emerald-50 font-medium text-emerald-800' : ''}`}
          >
            {allLabel}
            {value === allValue && <Check className="w-3.5 h-3.5" />}
          </button>
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              className={`w-full text-left px-2 py-1.5 text-xs rounded flex items-center justify-between hover:bg-slate-100 ${value === o.value ? 'bg-emerald-50 font-medium text-emerald-800' : ''}`}
              data-testid={`${testId}-opt-${o.value}`}
            >
              {o.label}
              {value === o.value && <Check className="w-3.5 h-3.5" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function Chamados() {
  const user = JSON.parse(localStorage.getItem('nautilus_user') || '{}');
  const [chamados, setChamados] = useState([]);
  const [estatisticas, setEstatisticas] = useState({ aberto: 0, em_analise: 0, pendente_usuario: 0, resolvido: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selecionado, setSelecionado] = useState(null);
  const [sheetAberto, setSheetAberto] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [filtroFeedback, setFiltroFeedback] = useState('todos');
  const [filtroUsuario, setFiltroUsuario] = useState('');
  const [filtroPeriodoPreset, setFiltroPeriodoPreset] = useState('todos');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [resposta, setResposta] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [atualizandoCampo, setAtualizandoCampo] = useState(false);
  const [anexoResposta, setAnexoResposta] = useState(null); // { file, preview }
  const fileInputRef = useRef(null);
  const historicoEndRef = useRef(null);

  useEffect(() => { historicoEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [selecionado]);

  const paramsFiltrosBase = () => {
    const params = {};
    if (filtroTipo !== 'todos') params.tipo = filtroTipo;
    if (filtroFeedback !== 'todos') params.origem_feedback = filtroFeedback;
    if (filtroUsuario.trim()) params.usuario = filtroUsuario.trim();
    if (filtroDataInicio) params.data_inicio = filtroDataInicio;
    if (filtroDataFim) params.data_fim = filtroDataFim;
    return params;
  };

  const carregarChamados = async () => {
    try {
      const params = paramsFiltrosBase();
      if (filtroStatus !== 'todos') params.status = filtroStatus;
      const res = await api.get('/tickets', { params });
      setChamados(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Erro ao carregar chamados');
    } finally {
      setLoading(false);
    }
  };

  const carregarEstatisticas = async () => {
    try {
      const res = await api.get('/tickets/estatisticas', { params: paramsFiltrosBase() });
      setEstatisticas(res.data);
    } catch {
      // Estatísticas são um complemento visual — falha aqui não deve travar a lista
    }
  };

  // Debounce único para todos os filtros (evita disparo duplicado e dá tempo pro campo de texto)
  useEffect(() => {
    const t = setTimeout(() => {
      carregarChamados();
      carregarEstatisticas();
    }, 350);
    return () => clearTimeout(t);
  }, [filtroStatus, filtroTipo, filtroFeedback, filtroUsuario, filtroDataInicio, filtroDataFim]);

  const filtrosAtivos = filtroStatus !== 'todos' || filtroTipo !== 'todos' || filtroFeedback !== 'todos' || !!filtroUsuario || filtroPeriodoPreset !== 'todos';

  const limparFiltros = () => {
    setFiltroStatus('todos');
    setFiltroTipo('todos');
    setFiltroFeedback('todos');
    setFiltroUsuario('');
    setFiltroPeriodoPreset('todos');
    setFiltroDataInicio('');
    setFiltroDataFim('');
  };

  const aplicarPeriodoPreset = (preset) => {
    setFiltroPeriodoPreset(preset);
    if (preset === 'personalizado') return; // usuário escolhe as datas manualmente no painel
    const { inicio, fim } = computeDateRange(preset);
    setFiltroDataInicio(inicio ? toDateInputValue(inicio) : '');
    setFiltroDataFim(fim ? toDateInputValue(fim) : '');
  };

  const alternarCardStatus = (statusKey) => {
    setFiltroStatus((atual) => (atual === statusKey ? 'todos' : statusKey));
  };

  const exportarCSV = () => {
    if (chamados.length === 0) {
      toast.error('Nenhum chamado para exportar com os filtros atuais');
      return;
    }
    const cabecalho = ['ID', 'Usuário', 'Email', 'Título', 'Tipo', 'Status', 'Prioridade', 'Origem Feedback', 'Aberto em', 'Atualizado em'];
    const linhas = chamados.map((c) => [
      c.id,
      c.usuario_nome || '',
      c.usuario_email || '',
      c.titulo || '',
      rotulo(TIPO_LABEL, c.tipo, '-'),
      rotulo(STATUS_LABEL, c.status, '-'),
      rotulo(PRIORIDADE_LABEL, c.prioridade, '-'),
      c.origem_feedback === 'negativo' ? 'Feedback Negativo' : c.origem_feedback === 'positivo' ? 'Feedback Positivo' : '-',
      formatarData(c.created_at),
      formatarData(c.updated_at),
    ]);
    const csv = [cabecalho, ...linhas]
      .map((linha) => linha.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chamados_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`${chamados.length} chamado(s) exportado(s) para CSV`);
  };

  const abrirChamado = async (id) => {
    try {
      const res = await api.get(`/tickets/${id}`);
      setSelecionado(res.data);
      setSheetAberto(true);
    } catch {
      toast.error('Erro ao abrir chamado');
    }
  };

  const fecharSheet = (aberto) => {
    setSheetAberto(aberto);
    if (!aberto) {
      setTimeout(() => setSelecionado(null), 200);
      setResposta('');
      setAnexoResposta(null);
    }
  };

  const enviarResposta = async () => {
    if ((!resposta.trim() && !anexoResposta) || !selecionado) return;
    setEnviando(true);
    try {
      let anexos = [];
      if (anexoResposta) {
        const formData = new FormData();
        formData.append('file', anexoResposta.file);
        const up = await api.post(`/tickets/${selecionado.id}/anexos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        anexos = [up.data];
      }
      const res = await api.post(`/tickets/${selecionado.id}/mensagens`, {
        mensagem: resposta.trim() || '(anexo enviado)',
        anexos,
      });
      setSelecionado(res.data);
      setResposta('');
      setAnexoResposta(null);
      toast.success('Resposta enviada ao usuário');
      carregarChamados();
      carregarEstatisticas();
    } catch {
      toast.error('Erro ao enviar resposta');
    } finally {
      setEnviando(false);
    }
  };

  const handleSelecionarAnexo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Arquivo muito grande (máximo 8MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAnexoResposta({ file, preview: reader.result });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const atualizarCampo = async (campo, valor) => {
    setAtualizandoCampo(true);
    try {
      const res = await api.put(`/tickets/${selecionado.id}`, { [campo]: valor });
      setSelecionado(res.data);
      carregarChamados();
      carregarEstatisticas();
    } catch {
      toast.error('Erro ao atualizar chamado');
    } finally {
      setAtualizandoCampo(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="chamados-page">
      {user.role !== 'admin' ? (
        <div className="text-center py-20 text-gray-400" data-testid="chamados-acesso-restrito">
          <LifeBuoy className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Esta página é exclusiva para administradores.</p>
        </div>
      ) : (
      <>
      <div className="flex items-center justify-between">
        <TitleWithHelp
          title="Chamados"
          description="Inbox de atendimento ao usuário. Qualquer pessoa pode abrir um chamado (geralmente escalado direto do chat do Nau IA). Responda aqui e o usuário verá sua resposta na mesma conversa do Nau IA."
        >
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-teal-600" /> Chamados
          </h1>
        </TitleWithHelp>
        <Button variant="outline" size="sm" onClick={() => { carregarChamados(); carregarEstatisticas(); }} data-testid="chamados-refresh-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Atualizar
        </Button>
      </div>

      {/* Dashboard clicável */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="chamados-dashboard-cards">
        {STATUS_CARDS.map((sc) => {
          const Icon = sc.icon;
          const ativo = filtroStatus === sc.key;
          return (
            <button
              key={sc.key}
              onClick={() => alternarCardStatus(sc.key)}
              data-testid={`chamados-stat-card-${sc.key}`}
              className={`text-left rounded-xl border p-4 transition-all ${sc.bg} ${ativo ? `${sc.border} ring-2 ${sc.ring} shadow-sm` : 'border-gray-100 hover:shadow-sm hover:-translate-y-0.5'}`}
            >
              <div className={`flex items-center gap-1.5 text-xs font-medium mb-1.5 ${sc.text}`}>
                <Icon className="w-3.5 h-3.5" /> {sc.label}
              </div>
              <p className="text-2xl font-bold text-slate-800">{estatisticas[sc.key] ?? 0}</p>
            </button>
          );
        })}
      </div>

      {/* Barra de Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 mb-3">
            <ListFilter className="w-3.5 h-3.5" /> FILTROS
          </div>
          <div className="flex flex-wrap items-center gap-2" data-testid="chamados-filtros-bar">
            {/* Período (pill + popover, presets + intervalo personalizado) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`h-9 ${filtroPeriodoPreset !== 'todos' ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'bg-white'}`}
                  data-testid="chamados-filtro-periodo-btn"
                >
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
                  {(PERIODOS.find((p) => p.id === filtroPeriodoPreset) || PERIODOS[0]).label}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2" align="start">
                <div className="space-y-0.5">
                  {PERIODOS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => aplicarPeriodoPreset(p.id)}
                      className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-slate-100 ${filtroPeriodoPreset === p.id ? 'bg-emerald-50 font-medium text-emerald-800' : ''}`}
                      data-testid={`chamados-filtro-periodo-${p.id}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {filtroPeriodoPreset === 'personalizado' && (
                  <div className="border-t pt-2 mt-2 space-y-2">
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase font-medium">De</label>
                      <Input
                        type="date"
                        value={filtroDataInicio}
                        onChange={(e) => setFiltroDataInicio(e.target.value)}
                        className="h-9 text-sm w-full"
                        data-testid="chamados-filtro-data-inicio"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-slate-500 uppercase font-medium">Até</label>
                      <Input
                        type="date"
                        value={filtroDataFim}
                        onChange={(e) => setFiltroDataFim(e.target.value)}
                        className="h-9 text-sm w-full"
                        data-testid="chamados-filtro-data-fim"
                      />
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>

            <FiltroPill
              label="Tipo"
              allLabel="Todos os tipos"
              value={filtroTipo}
              onChange={setFiltroTipo}
              options={Object.entries(TIPO_LABEL).map(([v, l]) => ({ value: v, label: l }))}
              testId="chamados-filtro-tipo"
            />
            <FiltroPill
              label="Status"
              allLabel="Todos os status"
              value={filtroStatus}
              onChange={setFiltroStatus}
              options={Object.entries(STATUS_LABEL).map(([v, l]) => ({ value: v, label: l }))}
              testId="chamados-filtro-status"
            />
            <FiltroPill
              label="Sentimento"
              allLabel="Todos"
              icon={ThumbsUp}
              value={filtroFeedback}
              onChange={setFiltroFeedback}
              options={[
                { value: 'negativo', label: '👎 Feedback Negativo' },
                { value: 'positivo', label: '👍 Feedback Positivo' },
              ]}
              testId="chamados-filtro-feedback"
            />
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-300 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                value={filtroUsuario}
                onChange={(e) => setFiltroUsuario(e.target.value)}
                placeholder="Usuário solicitante..."
                className="h-9 text-sm w-[190px] pl-7 bg-white"
                data-testid="chamados-filtro-usuario"
              />
            </div>
            <div className="flex gap-2 ml-auto">
              {filtrosAtivos && (
                <Button variant="ghost" size="sm" onClick={limparFiltros} className="h-9 text-slate-500" data-testid="chamados-limpar-filtros-btn">
                  <FilterX className="w-3.5 h-3.5 mr-1.5" /> Limpar
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={exportarCSV} className="h-9" data-testid="chamados-exportar-csv-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" /> Exportar CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de chamados */}
      <Card>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100" data-testid="chamados-lista">
            {loading ? (
              <div className="flex justify-center py-14"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : chamados.length === 0 ? (
              <div className="text-center py-14 text-sm text-slate-400">
                <Inbox className="w-8 h-8 mx-auto mb-2 text-slate-300" /> Nenhum chamado encontrado com os filtros atuais
              </div>
            ) : (
              chamados.map((c) => {
                const destacado = c.origem_feedback === 'negativo' || c.prioridade === 'urgente';
                return (
                  <button
                    key={c.id}
                    onClick={() => abrirChamado(c.id)}
                    className={`w-full text-left px-4 py-3.5 hover:bg-slate-50 transition-colors border-l-4 flex items-center gap-3.5 ${
                      destacado ? 'border-l-red-500 bg-red-50/40' : c.prioridade === 'alta' ? 'border-l-orange-400' : 'border-l-transparent'
                    }`}
                    data-testid={`chamado-item-${c.id}`}
                  >
                    <div className="h-9 w-9 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center text-xs font-semibold shrink-0">
                      {(c.usuario_nome || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate">{c.usuario_nome || 'Usuário desconhecido'}</span>
                        <span className="text-xs text-slate-400 shrink-0">{formatarData(c.created_at, true)}</span>
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">{c.titulo || '(sem título)'}</p>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                        <Badge className={`text-[10px] ${cor(STATUS_COLOR, c.status)}`}>{rotulo(STATUS_LABEL, c.status, 'Sem status')}</Badge>
                        <Badge className={`text-[10px] ${cor(TIPO_COLOR, c.tipo)}`}>{rotulo(TIPO_LABEL, c.tipo, 'Sem tipo')}</Badge>
                        <Badge className={`text-[10px] ${cor(PRIORIDADE_COLOR, c.prioridade)}`}>{rotulo(PRIORIDADE_LABEL, c.prioridade, 'Sem prioridade')}</Badge>
                        {c.origem_feedback === 'negativo' && (
                          <Badge className="text-[10px] bg-red-600 text-white border-red-600 flex items-center gap-0.5" data-testid={`chamado-tag-feedback-negativo-${c.id}`}>
                            <ThumbsDown className="w-2.5 h-2.5" /> Feedback Negativo
                          </Badge>
                        )}
                        {c.origem_feedback === 'positivo' && (
                          <Badge className="text-[10px] bg-emerald-600 text-white border-emerald-600 flex items-center gap-0.5">
                            <ThumbsUp className="w-2.5 h-2.5" /> Feedback Positivo
                          </Badge>
                        )}
                        {c.prioridade === 'urgente' && (
                          <Badge className="text-[10px] bg-red-700 text-white border-red-700 flex items-center gap-0.5">
                            <AlertTriangle className="w-2.5 h-2.5" /> Urgente
                          </Badge>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  </button>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Painel lateral (histórico + resposta) */}
      <Sheet open={sheetAberto} onOpenChange={fecharSheet}>
        <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col gap-0" data-testid="chamado-sheet">
          {selecionado && (
            <>
              {selecionado.origem_feedback === 'negativo' && (
                <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2 text-xs text-red-700 font-medium" data-testid="chamado-banner-feedback-negativo">
                  <ThumbsDown className="w-3.5 h-3.5" /> Chamado aberto automaticamente por feedback negativo (👎) numa resposta do Nau IA
                </div>
              )}
              <div className="p-4 pr-10 border-b border-gray-100 space-y-3">
                <div>
                  <p className="font-semibold text-slate-900 pr-4" data-testid="chamado-detalhe-titulo">{selecionado.titulo || '(sem título)'}</p>
                  <p className="text-xs text-slate-500">{selecionado.usuario_nome || 'Usuário desconhecido'} ({selecionado.usuario_email || 'sem e-mail'})</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={selecionado.tipo} onValueChange={(v) => atualizarCampo('tipo', v)} disabled={atualizandoCampo}>
                    <SelectTrigger data-testid="chamado-tipo-select" className="h-8 w-[150px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TIPO_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={selecionado.prioridade} onValueChange={(v) => atualizarCampo('prioridade', v)} disabled={atualizandoCampo}>
                    <SelectTrigger data-testid="chamado-prioridade-select" className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRIORIDADE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={selecionado.status} onValueChange={(v) => atualizarCampo('status', v)} disabled={atualizandoCampo}>
                    <SelectTrigger data-testid="chamado-status-select" className="h-8 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chamado-historico">
                {(selecionado.historico || []).map((m) => (
                  <div key={m.id} className={`flex ${m.role === 'admin' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 ${m.role === 'admin' ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-800'}`}>
                      <div className="flex items-center gap-1.5 mb-1 text-[11px] opacity-80">
                        {m.role === 'admin' ? <Headset className="w-3 h-3" /> : <User className="w-3 h-3" />}
                        {m.autor_nome || 'Usuário'} · {formatarData(m.created_at)}
                      </div>
                      <p className="text-sm whitespace-pre-wrap break-words">{m.mensagem}</p>
                      {(m.anexos || []).map((a) => (
                        a.tipo?.startsWith('image/') ? (
                          <a key={a.id || a.url} href={a.url} target="_blank" rel="noreferrer">
                            <img src={a.url} alt={a.nome || 'anexo'} className="rounded-lg mt-1.5 max-h-40 border border-white/20" />
                          </a>
                        ) : (
                          <a key={a.id || a.url} href={a.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs mt-1.5 underline opacity-90">
                            <Paperclip className="w-3 h-3" /> {a.nome || 'anexo'}
                          </a>
                        )
                      ))}
                    </div>
                  </div>
                ))}
                <div ref={historicoEndRef} />
              </div>

              <div className="p-3 border-t border-gray-100">
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleSelecionarAnexo} data-testid="chamado-anexo-input" />
                {anexoResposta && (
                  <div className="relative inline-block mb-2" data-testid="chamado-anexo-preview">
                    <img src={anexoResposta.preview} alt="Pré-visualização" className="h-14 rounded-lg border border-gray-200" />
                    <button type="button" onClick={() => setAnexoResposta(null)} className="absolute -top-1.5 -right-1.5 bg-gray-800 text-white rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="h-11 w-11 flex-shrink-0 rounded-lg border border-gray-200 text-gray-400 hover:text-teal-600 flex items-center justify-center"
                    data-testid="chamado-anexar-btn"
                  >
                    <Paperclip className="w-4 h-4" />
                  </button>
                  <Textarea
                    value={resposta}
                    onChange={(e) => setResposta(e.target.value)}
                    placeholder="Responder ao usuário..."
                    className="min-h-[44px] max-h-32 text-sm"
                    data-testid="chamado-resposta-textarea"
                  />
                  <Button onClick={enviarResposta} disabled={enviando || (!resposta.trim() && !anexoResposta)} data-testid="chamado-enviar-resposta-btn">
                    {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      </>
      )}
    </div>
  );
}
